import React, { useState, useEffect, isValidElement } from "react";

import { Tooltip } from "@mui/material";
import { FiEye, FiEdit, FiPrinter, FiTrash2 } from "react-icons/fi";
import { HiOutlineMail } from "react-icons/hi";

import TableCell from "../components/TableCell";
import { RowActions, Constants, RowActionsExtra, ChangeStatusButtonProps } from "../export";
import useStyles from "./styles";

type Props = {
  index: number;
  row: any;
  rowId: number;
  actions?: RowActions;
  rowActionsExtra?: RowActionsExtra[];
  endScrolled?: boolean;
  isOneElement?: boolean;
  stickyActions?: boolean;
};

const DEFAULT_ORDER_BY: string[] = [
  "handleEdit",
  "handleDetails",
  "handleChange",
  "handleCommunication",
  "handlePrint",
  "extraActions",
  "handleDelete",
];

const ChangeStatusButton = (props: ChangeStatusButtonProps) => {
  const { divProps, color = "#475467", size = 20, ...svgProps } = props;

  return (
    <div {...divProps}>
      <svg
        width={size.toString()}
        height={size.toString()}
        viewBox="0 0 15 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        {...svgProps}
      >
        <circle cx="8.33337" cy="8.63329" r="5.96667" stroke={color} />
        <g clipPath="url(#clip0_12330_95944)">
          <path
            d="M4.19995 7.12501V6.87501C4.19995 6.6679 4.36784 6.50001 4.57495 6.50001H10.2V5.75001C10.2 5.41615 10.6046 5.24935 10.8401 5.48484L12.0901 6.73484C12.2366 6.88129 12.2366 7.11873 12.0901 7.26516L10.8401 8.51516C10.6055 8.74973 10.2 8.58526 10.2 8.25001V7.50001H4.57495C4.36784 7.50001 4.19995 7.33212 4.19995 7.12501ZM11.825 9.50001H6.19995V8.75001C6.19995 8.41698 5.79586 8.24876 5.55978 8.48484L4.30978 9.73484C4.16334 9.88129 4.16334 10.1187 4.30978 10.2652L5.55978 11.5152C5.79459 11.75 6.19995 11.585 6.19995 11.25V10.5H11.825C12.0321 10.5 12.2 10.3321 12.2 10.125V9.87501C12.2 9.6679 12.0321 9.50001 11.825 9.50001Z"
            fill={color}
          />
        </g>
        <defs>
          <clipPath id="clip0_12330_95944">
            <rect
              width={size.toString()}
              height={size.toString()}
              fill="white"
              transform="translate(4.19995 4.5)"
            />
          </clipPath>
        </defs>
      </svg>
    </div>
  );
};

const GridActions = (props: Props) => {
  const { index, row, rowId, actions, rowActionsExtra, endScrolled, stickyActions, isOneElement } =
    props;

  const classes = useStyles();

  const [orderBy, setOrderBy] = useState<string[]>();

  useEffect(() => {
    setOrderBy(actions?.orderBy || DEFAULT_ORDER_BY);
  }, [actions?.orderBy]);

  const renderDetails = (key: string) => {
    if (actions && actions?.handleDetails) {
      return (
        <Tooltip key={key} data-cy="action-details" title="Details" placement="top">
          <div className={classes.item}>
            <FiEye
              color={Constants.defaultColorIcon}
              size={Constants.defaultRowActionSize}
              onClick={() => actions.handleDetails && actions.handleDetails(String(rowId), row)}
            />
          </div>
        </Tooltip>
      );
    }

    return null;
  };

  const renderEdit = (key: string) => {
    if (actions && actions?.handleEdit) {
      return (
        <Tooltip key={key} data-cy="action-edit" title="Edit" placement="top">
          <div className={classes.item}>
            <FiEdit
              color={Constants.defaultColorIcon}
              size={Constants.defaultRowActionSize}
              onClick={() => actions.handleEdit && actions.handleEdit(String(rowId), row)}
            />
          </div>
        </Tooltip>
      );
    }

    return null;
  };

  const renderPrint = (key: string) => {
    if (actions && actions?.handlePrint) {
      return (
        <Tooltip key={key} data-cy="action-print" title="Print" placement="top">
          <div className={classes.item}>
            <FiPrinter
              color={Constants.defaultColorIcon}
              size={Constants.defaultRowActionSize}
              onClick={() => actions.handlePrint && actions.handlePrint(String(rowId), row)}
            />
          </div>
        </Tooltip>
      );
    }

    return null;
  };

  const renderChange = (key: string) => {
    if (actions && actions?.handleChange) {
      return (
        <Tooltip key={key} data-cy="action-change" title="Change status" placement="top">
          <div className={classes.item}>
            <ChangeStatusButton
              onClick={() => actions.handleChange && actions.handleChange(String(rowId), row)}
            />
          </div>
        </Tooltip>
      );
    }

    return null;
  };

  const renderCommunication = (key: string) => {
    if (actions && actions?.handleCommunication) {
      return (
        <Tooltip key={key} data-cy="action-communication" title={"Communication"} placement="top">
          <div className={classes.item}>
            <HiOutlineMail
              color={Constants.defaultColorIcon}
              size={Constants.defaultRowActionSize}
              onClick={() =>
                actions.handleCommunication && actions.handleCommunication(String(rowId), row)
              }
            />
          </div>
        </Tooltip>
      );
    }

    return null;
  };

  const renderExtraAction = (extraAction: RowActionsExtra, key: string) => {
    if (extraAction?.buttonAndAction && isValidElement(extraAction?.buttonAndAction(rowId, row))) {
      return (
        <div key={key} className={classes.itemExtra}>
          {extraAction?.buttonAndAction(rowId, row)}
        </div>
      );
    }

    if (extraAction?.button && isValidElement(extraAction?.button)) {
      return (
        <div
          key={key}
          className={classes.itemExtra}
          onClick={() => extraAction?.action && extraAction?.action(String(rowId), row)}
        >
          {extraAction?.button}
        </div>
      );
    }

    return null;
  };

  const renderExtraActions = () => {
    if (rowActionsExtra && rowActionsExtra.length > 0) {
      return rowActionsExtra.map((extraAction) => {
        return renderExtraAction(extraAction, `extra${extraAction?.key}`);
      });
    }
  };

  const renderItemExtraActions = (key: string) => {
    if (rowActionsExtra && rowActionsExtra.length > 0) {
      return (
        <div key={key} data-cy="action-extra" className={classes.mainDiv}>
          {renderExtraActions()}
        </div>
      );
    }

    return null;
  };

  const renderDelete = (key: string) => {
    if (actions && actions?.handleDelete) {
      return (
        <Tooltip key={key} data-cy="action-delete" title="Delete" placement="top">
          <div className={classes.item}>
            <FiTrash2
              color={"rgb(207, 42, 39)"}
              size={Constants.defaultRowActionSize}
              onClick={() => actions.handleDelete && actions.handleDelete(String(rowId), row)}
            />
          </div>
        </Tooltip>
      );
    }

    return null;
  };

  const getExtraActionByKey = (action: string, key: string) => {
    if (rowActionsExtra && rowActionsExtra?.length > 0) {
      const extraActionFound = rowActionsExtra?.find((extraAction) => extraAction?.key === action);

      if (extraActionFound) return renderExtraAction(extraActionFound, key);
    }

    return null;
  };

  const getComponentByAction = (action: string) => {
    const key = `tooltip${action}`;

    if (actions || rowActionsExtra) {
      switch (action) {
        case "handleDetails":
          return renderDetails(key);
        case "handleEdit":
          return renderEdit(key);
        case "handlePrint":
          return renderPrint(key);
        case "handleDelete":
          return renderDelete(key);
        case "handleChange":
          return renderChange(key);
        case "handleCommunication":
          return renderCommunication(key);
        case "extraActions":
          return renderItemExtraActions(key);
        default:
          return getExtraActionByKey(action, key);
      }
    }
  };

  const renderActions = () => {
    if (orderBy && orderBy?.length > 0) {
      return orderBy?.map((action) => {
        return getComponentByAction(action);
      });
    }

    return null;
  };

  return (
    <TableCell
      endScrolled={endScrolled}
      isOneElement={isOneElement}
      sticky={stickyActions}
      index={index}
      data-cy="table-cell-actions"
      width="150px"
      isAction
    >
      <div data-cy="actions" className={classes.mainDiv}>
        {renderActions()}
      </div>
    </TableCell>
  );
};

export { FiEye, FiEdit, FiPrinter, FiTrash2, HiOutlineMail, ChangeStatusButton };
export default GridActions;
